import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const EFFECTIVE_DATE = "May 12, 2026";

type Subprocessor = {
  name: string;
  purpose: string;
  dataCategories: string;
  location: string;
  policyUrl: string;
};

const SUBPROCESSORS: Subprocessor[] = [
  {
    name: "Clerk, Inc.",
    purpose: "Authentication, identity, MFA, organization membership.",
    dataCategories:
      "Email address, name, profile image, authentication credentials (passwords / SSO tokens / MFA factors), IP address, user agent.",
    location: "United States",
    policyUrl: "https://clerk.com/legal/privacy",
  },
  {
    name: "Stripe, Inc.",
    purpose: "Payments, subscription billing, customer portal, invoices.",
    dataCategories:
      "Billing name and address, email, payment card details (held by Stripe — we do not store card numbers), tax ID where applicable, subscription plan, transaction history.",
    location: "United States",
    policyUrl: "https://stripe.com/privacy",
  },
  {
    name: "Resend",
    purpose:
      "Transactional and notification email (auth codes, contract notifications, lifecycle reminders, demo verification codes).",
    dataCategories:
      "Recipient email address, sender, subject and message body, delivery and bounce events.",
    location: "United States",
    policyUrl: "https://resend.com/legal/privacy-policy",
  },
  {
    name: "Google Cloud Storage",
    purpose:
      "File and document storage for uploaded contract PDFs, clause images, and other attachments.",
    dataCategories:
      "Files you upload to the Service, plus file metadata (name, size, content type, upload time, uploader user ID).",
    location: "United States",
    policyUrl: "https://cloud.google.com/terms/cloud-privacy-notice",
  },
  {
    name: "Replit, Inc.",
    purpose:
      "Application hosting (Replit Deployments) and primary database (Replit Managed Postgres) including automated daily backups.",
    dataCategories:
      "All Customer Data and account data necessary to operate the Service.",
    location: "United States",
    policyUrl: "https://replit.com/site/privacy",
  },
  {
    name: "OpenAI, L.L.C.",
    purpose:
      "AI contract extraction (Chat Completions API) — used only when you trigger the PDF import / AI extraction feature.",
    dataCategories:
      "Contents of the uploaded contract document. Under our API agreement, submitted data is not used to train models.",
    location: "United States",
    policyUrl: "https://openai.com/policies/privacy-policy",
  },
];

export default function Subprocessors() {
  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      <nav className="sticky top-0 z-50 border-b border-gray-100 bg-white/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <img src="/logo.svg" alt="Zindeal" className="w-8 h-8 rounded-full" />
            <span className="font-serif font-semibold text-lg tracking-tight">Zindeal</span>
          </Link>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link href="/privacy">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Privacy Policy
              </Link>
            </Button>
            <Button size="sm" className="bg-[#0B1834] hover:bg-[#0A152E] text-white" asChild>
              <Link href="/sign-up">Get Started</Link>
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-10">
          <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 mb-3">
            Subprocessors
          </h1>
          <p className="text-gray-500 text-sm">Effective date: {EFFECTIVE_DATE}</p>
          <p className="mt-4 text-gray-600 leading-relaxed">
            Zebiso Intelligence, Inc. uses the following third-party service providers
            ("subprocessors") to operate the Service. We require each subprocessor to
            maintain at least the same level of privacy and security protection as
            described in our{" "}
            <Link href="/privacy" className="text-[#0B1834] font-medium underline">
              Privacy Policy
            </Link>{" "}
            and to use Customer Data only to provide the contracted service.
          </p>
          <p className="mt-3 text-gray-600 leading-relaxed">
            We will provide at least 30 days' notice of any new subprocessor by
            updating this page. To receive email notifications of changes, contact{" "}
            <a
              href="mailto:privacy@zindeal.com"
              className="text-[#0B1834] font-medium underline"
            >
              privacy@zindeal.com
            </a>
            .
          </p>
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  Subprocessor
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  Purpose
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  Data categories
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  Location
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {SUBPROCESSORS.map((sp) => (
                <tr key={sp.name} className="align-top">
                  <td className="px-4 py-4 font-medium text-gray-900">
                    <a
                      href={sp.policyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#0B1834] underline underline-offset-2"
                    >
                      {sp.name}
                    </a>
                  </td>
                  <td className="px-4 py-4 text-gray-700">{sp.purpose}</td>
                  <td className="px-4 py-4 text-gray-700">{sp.dataCategories}</td>
                  <td className="px-4 py-4 text-gray-700 whitespace-nowrap">
                    {sp.location}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <section className="mt-12 rounded-xl border border-gray-200 bg-gray-50 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Data Processing Addendum (DPA)</h2>
          <p className="text-sm text-gray-700 leading-relaxed">
            B2B customers who require a signed Data Processing Addendum can request
            our DPA template by emailing{" "}
            <a
              href="mailto:privacy@zindeal.com"
              className="text-[#0B1834] font-medium underline"
            >
              privacy@zindeal.com
            </a>
            . The template covers GDPR Art. 28 processor obligations, the Standard
            Contractual Clauses for international transfers, and the subprocessor
            list above.
          </p>
        </section>
      </main>

      <footer className="border-t border-gray-100 py-8 px-6 mt-16">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Zindeal" className="w-6 h-6 rounded-full" />
            <span className="font-semibold text-gray-700">Zindeal</span>
          </div>
          <div className="flex gap-6">
            <Link href="/terms" className="hover:text-gray-700 transition-colors">
              Terms of Service
            </Link>
            <Link href="/privacy" className="hover:text-gray-700 transition-colors">
              Privacy Policy
            </Link>
            <Link
              href="/subprocessors"
              className="hover:text-gray-700 transition-colors font-medium text-[#0B1834]"
            >
              Subprocessors
            </Link>
            <Link href="/security" className="hover:text-gray-700 transition-colors">
              Security
            </Link>
          </div>
          <span>© {new Date().getFullYear()} Zebiso Intelligence, Inc. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
